<script setup lang="ts">
/**
 * ToastItem — 单条 Toast 渲染组件
 *
 * Props:
 *   - item: ToastItem
 *
 * Emits:
 *   - dismiss(id: string)
 *
 * 触屏友好：高度 ≥ 64px，字体 ≥ 28px
 * 动画：CSS transition（进入 slide-down + fade-in / 退出 slide-up + fade-out）
 */
import type { ToastItem } from './useToast'

defineOptions({ name: 'ToastItem' })

const props = defineProps<{ item: ToastItem }>()

const emit = defineEmits<{
  dismiss: [id: string]
}>()

// variant → 样式 + 图标映射
const variantConfig = {
  success: {
    containerClass: 'bg-green-600 border-green-700',
    icon: '✅',
    iconAriaLabel: '成功',
  },
  info: {
    containerClass: 'bg-blue-600 border-blue-700',
    icon: 'ℹ️',
    iconAriaLabel: '信息',
  },
  warn: {
    containerClass: 'bg-yellow-500 border-yellow-600',
    icon: '⚠️',
    iconAriaLabel: '警告',
  },
  error: {
    containerClass: 'bg-red-600 border-red-700',
    icon: '❌',
    iconAriaLabel: '错误',
  },
  loading: {
    containerClass: 'bg-gray-700 border-gray-800',
    icon: '⏳',
    iconAriaLabel: '加载中',
  },
} as const

const config = variantConfig[props.item.variant] ?? variantConfig.info
</script>

<template>
  <div
    :data-testid="`toast-item-${item.variant}`"
    :data-toast-id="item.id"
    role="alert"
    :aria-label="item.message"
    :class="[
      'flex min-h-[64px] items-center gap-4 rounded-xl border px-6 py-4',
      'text-white shadow-2xl',
      'pointer-events-auto',
      config.containerClass,
    ]"
  >
    <!-- 图标 -->
    <span class="shrink-0 text-3xl" :aria-label="config.iconAriaLabel" role="img">
      {{ config.icon }}
    </span>

    <!-- 消息文字（≥ 28px → text-[28px]） -->
    <span class="flex-1 text-[28px] font-medium leading-snug">
      {{ item.message }}
    </span>

    <!-- 关闭按钮（可选） -->
    <button
      v-if="item.closable"
      class="shrink-0 rounded-lg p-2 text-2xl text-white/80 hover:text-white focus:outline-none"
      :aria-label="'关闭'"
      data-testid="toast-close-btn"
      @click="emit('dismiss', item.id)"
    >
      ✕
    </button>
  </div>
</template>
